import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query';
import { AppState } from '../redux/store';
import { setTokens } from '../redux/tokenSlice';
import { logger } from '../services/logger';
import { enqueueError } from '../redux/errorSlice';

const baseConfig = {
  baseUrl: `${process.env.REACT_APP_API_HOST}/api/v1`,
  headers: {
    Accept: 'application/json',
  },
} satisfies Parameters<typeof fetchBaseQuery>[0];

export const apiBaseQuery = fetchBaseQuery(baseConfig);

const apiAuthenticatedBaseQuery = fetchBaseQuery({
  ...baseConfig,
  prepareHeaders(headers, { getState }) {
    // fetchBaseQuery.prepareHeaders は Redux から独立していて
    // 型情報を自動的に導出・参照できないので as を例外的に使用する
    const token = (getState() as AppState).tokens;
    headers.append('Authorization', `Bearer ${token.accessToken}`);
  },
  fetchFn: (...args) => {
    const p = fetch(...removeInternalHeaders(args));
    if (isFetchTrackTarget(args)) fetchTrackStore.add(p);
    return p;
  },
});
type RefreshTokenResponse = {
  accessToken: string;
};

// トークンリフレッシュの多重リクエストを避けるための簡易セマフォ機構
/**
 * トークンリフレッシュ操作がロックされているか
 */
let refreshingLocked = false;
type Resolver = {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
};
const resolvers: Resolver[] = [];
/**
 * 更新操作のロック状態を待機するための async 関数
 */
const waitForRefreshingUnlocked = () =>
  new Promise((resolve, reject) => {
    // ロックされていなければ即時解決
    if (!refreshingLocked) {
      resolve(undefined);
      return;
    }
    resolvers.push({ resolve, reject });
  });
/**
 * 更新操作をロックする
 */
const lock = async () => {
  await waitForRefreshingUnlocked();
  refreshingLocked = true;
};
/**
 * 更新操作のロックを解除する
 */
const resolveAll = () => {
  refreshingLocked = false;
  resolvers.forEach(({ resolve }) => resolve());
  resolvers.splice(0, resolvers.length);
};
/**
 * 更新操作のロックをrejectで解除する
 */
const rejectAll = () => {
  refreshingLocked = false;
  resolvers.forEach(({ reject }) =>
    reject(Error('Failed to token refreshing in the previous attempt'))
  );
  resolvers.splice(0, resolvers.length);
};

export const apiAuthenticatedQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const attempt = async () =>
    apiAuthenticatedBaseQuery(args, api, extraOptions);

  await waitForRefreshingUnlocked();
  let res = await attempt();
  if (res.error && res.error.status === 401) {
    if (refreshingLocked) {
      await waitForRefreshingUnlocked();
      res = await attempt();
    } else {
      await lock();
      // トークンリフレッシュ API リクエスト中は新たにリクエストを実行しない
      const state = api.getState() as AppState;
      const refreshResult = await apiAuthenticatedBaseQuery(
        {
          url: `/auth/access-token`,
          method: 'POST',
          body: {
            refreshToken: state.tokens.refreshToken,
          },
        },
        api,
        extraOptions
      );
      const tokens = refreshResult.data as RefreshTokenResponse;
      if (tokens) {
        api.dispatch(
          setTokens({
            ...state.tokens,
            ...tokens,
          })
        );
        res = await attempt();
        resolveAll();
      } else {
        logger.warn('Failed to refresh', refreshResult.error);
        if (refreshResult.error) {
          api.dispatch(enqueueError(refreshResult.error));
        }
        rejectAll();
      }
    }
  }
  return res;
};

/** (RTK Query を使って) fetch を実行中かどうかを保持する */
export const fetchTrackStore = createFetchTrackStore();

type Subscriber = () => void;

function createFetchTrackStore() {
  let counter = 0;
  let subscribers: Subscriber[] = [];

  const notify = () => {
    subscribers.forEach((s) => s());
  };

  return {
    /** fetch 実行中の Promise を追加する */
    add(p: Promise<unknown>): void {
      counter += 1;
      notify();
      p.finally(() => {
        counter -= 1;
        notify();
      });
    },

    /** 実行中の Promise の数を返す */
    count(): number {
      return counter;
    },

    /** `count()` の結果が変わったときに呼び出すコールバックを登録する */
    subscribe(subscriber: Subscriber): () => void {
      subscribers = [...subscribers, subscriber];
      return () => {
        subscribers = subscribers.filter((s) => s !== subscriber);
      };
    },
  };
}

/** fetch 実行中かを監視する対象かどうか */
function isFetchTrackTarget(args: Parameters<typeof fetch>): boolean {
  // 明示的にローディング表示が不要だと指定されているものは監視しない
  if (getRequestHeader(args, loadingSupressMarker) === 'suppress') return false;
  return true;
}

type InternalMarker = `x-internal-${string}`;

/**
 * ローティング表示をしないリクエストであることを示すマーカー
 *
 * XxxWithoutLoading 系のエンドポイントで使用される想定。
 * 最終的なリクエストのヘッダーには含まれないようにする。
 */
const loadingSupressMarker =
  'x-internal-loading' as const satisfies InternalMarker;

/**
 * 内部的なフラグの伝達のために特殊な使い方として用いられるヘッダーを生成する
 */
export function internalHeaders({
  loading,
}: {
  /** このリクエストに対するローディング表示 */
  loading?: /** 表示しない */ 'suppress';
}): Record<InternalMarker, string> {
  return {
    ...(loading ? { [loadingSupressMarker]: loading } : {}),
  };
}

function getRequestHeader(
  [info, init]: Parameters<typeof fetch>,
  key: string
): string | undefined {
  if (init && init.headers) {
    if (init.headers instanceof Headers) {
      const v = init.headers.get(key);
      if (v) return v;
    } else if (Array.isArray(init.headers)) {
      const v = init.headers.find(([k]) => k.toLowerCase() === key);
      if (v) return v[1];
    } else {
      const v = Object.entries(init.headers).find(
        ([k]) => k.toLocaleLowerCase() === key
      );
      if (v) return v[1];
    }
  }
  if (info instanceof Request) {
    const v = info.headers.get(key);
    if (v) return v;
  }
}

function removeInternalHeaders([info, init]: Parameters<
  typeof fetch
>): Parameters<typeof fetch> {
  const isInternalMarker = (key: string): boolean =>
    key.toLowerCase().startsWith('x-internal-');

  if (init && init.headers) {
    if (init.headers instanceof Headers) {
      const headers = new Headers(
        Array.from(init.headers.entries()).filter(([k]) => !isInternalMarker(k))
      );
      return [info, { ...init, headers }];
    } else if (Array.isArray(init.headers)) {
      const headers = init.headers.filter(([k]) => !isInternalMarker(k));
      return [info, { ...init, headers }];
    } else {
      const headers = Object.fromEntries(
        Object.entries(init.headers).filter(([k]) => !isInternalMarker(k))
      );
      return [info, { ...init, headers }];
    }
  }
  if (info instanceof Request) {
    const req = info.clone();
    for (const [k] of Array.from(req.headers.entries())) {
      if (isInternalMarker(k)) req.headers.delete(k);
    }
    return [req, init];
  }
  return [info, init];
}
